import React, { useEffect, useState } from 'react';
import { Loader2, Save, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';

const isoFromRecord = (d) => {
  if (!d) return new Date().toISOString().slice(0, 10);
  if (d.post_date) return d.post_date;
  if (d.created_at) return String(d.created_at).slice(0, 10);
  return new Date().toISOString().slice(0, 10);
};

export default function DosageEditDialog({ open, onOpenChange, dosage, onSaved }) {
  const [title, setTitle] = useState('');
  const [verseRef, setVerseRef] = useState('');
  const [verse, setVerse] = useState('');
  const [body, setBody] = useState('');
  const [minutes, setMinutes] = useState('3');
  const [postDate, setPostDate] = useState('');
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!dosage) return;
    setTitle(dosage.title || '');
    setVerseRef(dosage.verse_ref || '');
    setVerse(dosage.verse || '');
    setBody(dosage.body || '');
    setMinutes(String(dosage.minutes || 3));
    setPostDate(isoFromRecord(dosage));
    setImage(null);
  }, [dosage]);

  const submit = async (e) => {
    e.preventDefault();
    if (!dosage) return;
    if (!title || !body) { toast({ title: 'Title and body are required' }); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('verse_ref', verseRef);
      fd.append('verse', verse);
      fd.append('body', body);
      fd.append('minutes', String(parseInt(minutes || '3', 10) || 3));
      fd.append('post_date', postDate);
      if (image) fd.append('image', image);
      await api.updateDosage(dosage.id, fd);
      toast({ title: 'Dosage updated' });
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Update failed', description: String(err?.response?.data?.detail || err.message) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Pencil className="h-5 w-5 text-teal-deep" /> Edit Dosage
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="edt">Title *</Label>
            <Input id="edt" data-testid="edit-dosage-title" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="evr">Verse Reference</Label>
              <Input id="evr" value={verseRef} onChange={e => setVerseRef(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="emin">Read time (min)</Label>
              <Input id="emin" type="number" min="1" max="30" value={minutes} onChange={e => setMinutes(e.target.value)} />
            </div>
          </div>
          <div>
            <Label htmlFor="epd">Post date</Label>
            <Input id="epd" data-testid="edit-dosage-date" type="date" value={postDate} onChange={e => setPostDate(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ev">Verse text</Label>
            <Textarea id="ev" rows={2} value={verse} onChange={e => setVerse(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="eb">Devotional body *</Label>
            <Textarea id="eb" rows={6} value={body} onChange={e => setBody(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ei">Replace cover image (optional)</Label>
            <Input id="ei" type="file" accept="image/*" onChange={e => setImage(e.target.files?.[0] || null)} />
            {image && <p className="text-xs text-ink/50 mt-1">{image.name}</p>}
          </div>
          <div className="flex gap-2">
            <Button type="submit" data-testid="edit-dosage-save" disabled={busy} className="flex-1 bg-teal-brand hover:bg-teal-deep text-white h-11">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Changes
            </Button>
            <Button type="button" variant="outline" className="h-11" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
